import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { redeemPromoCode, PromoRedeemError } from '../services/api';
import { capturePromoFromUrl, clearPendingPromo } from '../utils/promo';
import { normalizePromoCode } from '../../shared/types';

/**
 * Encaisse le code promo dès que la session est ouverte.
 *
 * C'est la dernière marche du parcours « carte de visite » : le visiteur a
 * scanné, saisi son email, tapé son code à 6 chiffres. Il ne doit plus rien
 * avoir à faire — les analyses sont créditées pendant qu'il arrive sur l'écran
 * de choix du type d'analyse.
 *
 * Le code est cherché dans les deux porteurs posés en amont (cf. utils/promo) :
 * le localStorage de l'onglet, puis le champ `pendingPromoCode` du profil
 * (posé par le serveur à l'inscription, ex-`user_metadata.promo_code`) — ce
 * dernier couvrant l'ouverture de session dans un autre navigateur (lien
 * ouvert depuis l'application mail). Le serveur reste seul juge de la validité.
 */

export type PromoNoticeKind = 'success' | 'info' | 'error';

export interface PromoNotice {
  kind: PromoNoticeKind;
  title: string;
  message: string;
}

/** Statuts sur lesquels il est inutile de réessayer : le verdict ne changera
 *  pas au prochain chargement. On oublie le code pour ne pas rejouer l'appel
 *  — ni réafficher le message — à chaque visite. */
const TERMINAL_STATUSES = new Set([
  'not_found',
  'expired',
  'exhausted',
  'already_redeemed',
  'email_already_redeemed',
  'plan_not_eligible',
]);

export function usePromoRedemption() {
  const { session, user, refreshProfile } = useAuth();
  const [notice, setNotice] = useState<PromoNotice | null>(null);
  const [redeeming, setRedeeming] = useState(false);
  // Empêche le double appel du StrictMode en dev, et tout rejeu si l'effet
  // se relance (refreshProfile change `user`, donc l'effet re-tourne).
  const attempted = useRef(false);

  const isAuthenticated = !!session && !!user;

  useEffect(() => {
    if (!isAuthenticated || attempted.current) return;

    const metadataCode = normalizePromoCode(user?.pendingPromoCode);
    // capture… plutôt que simple lecture : un habitué déjà connecté qui scanne
    // la carte n'a jamais vu l'écran de connexion, donc personne d'autre n'aura
    // ramassé le `?c=` de son URL.
    const stored = capturePromoFromUrl();
    const code = stored || metadataCode;
    if (!code) return;

    // Le canal se déduit du porteur : un code encore en localStorage vient du
    // QR scanné dans cet onglet, sinon il a traversé via l'inscription.
    const source = stored ? 'qr' : 'signup';

    attempted.current = true;
    setRedeeming(true);

    redeemPromoCode(code, source)
      .then(async (result) => {
        clearPendingPromo();
        // Le plan de l'org vient de passer en `beta` : sans ce rafraîchissement,
        // l'utilisateur resterait sur l'écran d'onboarding (paywall) alors que
        // ses analyses sont déjà créditées.
        await refreshProfile();
        setNotice({
          kind: 'success',
          title: `${result.analysesGranted} analyse${result.analysesGranted > 1 ? 's' : ''} offerte${result.analysesGranted > 1 ? 's' : ''} !`,
          message:
            "C'est crédité sur votre compte. Choisissez le type d'analyse qui vous intéresse pour commencer.",
        });
      })
      .catch((err: unknown) => {
        const status = err instanceof PromoRedeemError ? err.status : 'unknown';
        const message = err instanceof Error ? err.message : "L'échange du code a échoué.";

        if (TERMINAL_STATUSES.has(status)) {
          clearPendingPromo();
          // `plan_not_eligible` n'est pas un échec : c'est un client qui a déjà
          // mieux que l'offre. Le ton doit le refléter.
          setNotice({
            kind: status === 'plan_not_eligible' ? 'info' : 'error',
            title: status === 'plan_not_eligible' ? 'Rien à ajouter' : 'Code non appliqué',
            message,
          });
          return;
        }

        // Incident passager (réseau, 500, rate limit) : on GARDE le code en
        // attente et on autorise une nouvelle tentative au prochain montage.
        // Perdre 10 analyses sur un timeout serait le pire scénario au salon.
        attempted.current = false;
        setNotice({
          kind: 'error',
          title: 'Code non appliqué',
          message: `${message} Rechargez la page ou saisissez le code dans Facturation.`,
        });
      })
      .finally(() => setRedeeming(false));
  }, [isAuthenticated, user?.pendingPromoCode, refreshProfile]);

  const dismiss = useCallback(() => setNotice(null), []);

  return { notice, redeeming, dismiss };
}
